import {
  BrainIcon,
  Building2Icon,
  CalendarIcon,
  ChartNoAxesCombinedIcon,
  DatabaseIcon,
  FileOutputIcon,
  HeartHandshakeIcon,
  HomeIcon,
  LayersIcon,
  LayoutDashboardIcon,
  type LucideIcon,
  MapPinnedIcon,
  MessageSquareTextIcon,
  PieChartIcon,
  SearchIcon,
  SettingsIcon,
  SirenIcon,
  SquareTerminal,
  TrendingUpIcon,
  UsersIcon,
} from "lucide-react";

import type { Team } from "@/store/useTeamStore";

export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  // true 면 user.role === "admin" 일 때만 노출한다.
  adminOnly?: boolean;
  items?: { title: string; url: string }[];
}

// 사이드바의 한 묶음. 메뉴가 길어질수록 label 로 맥락을 잡아준다.
export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

// 팀 목록. TeamSwitcher 헤더와 TEAM_NAV 조회의 원본.
export const TEAMS: Team[] = [
  {
    id: "mindcast",
    name: "MindCast",
    logo: BrainIcon,
    plan: "정신건강 트렌드 모니터링",
  },
  {
    id: "ncmh",
    name: "국립정신건강센터",
    logo: Building2Icon,
    plan: "공공기관 협력",
  },
  {
    id: "regional",
    name: "지역 모니터링팀",
    logo: UsersIcon,
    plan: "지역별 현황",
  },
];

// 전체 메뉴를 업무 흐름(개요 → 분석 → 모니터링 → 리포트 → 관리) 단위로 묶은 원본.
// 팀별 메뉴는 이 목록에서 url 로 골라내므로, 새 페이지는 여기에만 추가하면 된다.
export const NAV_SECTIONS: NavSection[] = [
  {
    id: "overview",
    label: "개요",
    items: [
      { title: "홈", url: "/home-new", icon: HomeIcon },
      { title: "대시보드", url: "/dashboard", icon: LayoutDashboardIcon },
      { title: "기존 대시보드", url: "/home", icon: SquareTerminal },
    ],
  },
  {
    id: "analysis",
    label: "여론 · 감정 분석",
    items: [
      { title: "국민 마음 지표", url: "/mind", icon: HeartHandshakeIcon },
      {
        title: "국내 여론",
        url: "/public-opinion",
        icon: ChartNoAxesCombinedIcon,
      },
      {
        title: "여론 감정 분석",
        url: "/sentiment-analysis",
        icon: MessageSquareTextIcon,
      },
      {
        title: "감정 분석 미리보기",
        url: "/emotion-preview",
        icon: PieChartIcon,
      },
      {
        // 메뉴(헤더·사이드바)와 페이지 제목이 같은 이름을 쓴다. "감정 키워드
        // 랭크" 였을 때는 눌러서 도착한 화면의 제목이 "감정 키워드" 라 둘이
        // 서로 달랐다.
        title: "감정 키워드",
        url: "/emotion-keywords",
        icon: TrendingUpIcon,
      },
      { title: "키워드 검색", url: "/keyword-search", icon: SearchIcon },
    ],
  },
  {
    id: "monitoring",
    label: "모니터링 · 경보",
    items: [
      {
        title: "지역별 현황 모니터링",
        url: "/regional-monitoring",
        icon: MapPinnedIcon,
      },
      {
        title: "데이터 통합 모니터링",
        url: "/data-integration-monitoring",
        icon: LayersIcon,
      },
      {
        title: "조기경보 시스템",
        url: "/early-warning-system",
        icon: SirenIcon,
      },
      {
        title: "소셜 이벤트",
        url: "/social-event",
        icon: CalendarIcon,
        items: [
          { title: "이벤트 목록", url: "/social-event" },
          { title: "카테고리", url: "/social-event/category" },
        ],
      },
    ],
  },
  {
    id: "report",
    label: "리포트",
    items: [
      {
        title: "보고서 자동 생성",
        url: "/auto-generate-report",
        icon: FileOutputIcon,
      },
    ],
  },
  {
    id: "management",
    label: "데이터 · 설정",
    items: [
      {
        title: "데이터 관리",
        url: "/data-management",
        icon: DatabaseIcon,
        adminOnly: true,
      },
      { title: "설정", url: "/settings", icon: SettingsIcon, adminOnly: true },
    ],
  },
];

// 모든 팀이 공통으로 보는 페이지(기존 메뉴 + 관리자 설정).
const COMMON_URLS = [
  "/home",
  "/mind",
  "/public-opinion",
  "/data-management",
  "/settings",
];

// 노출할 url 만 남기고 빈 묶음은 버린다(묶음/항목 순서는 NAV_SECTIONS 기준).
const pickSections = (urls: string[]): NavSection[] => {
  const allowed = new Set([...urls, ...COMMON_URLS]);
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => allowed.has(item.url)),
  })).filter((section) => section.items.length > 0);
};

// 팀 id → 해당 팀에 노출할 묶음 목록.
// (팀별 페이지 구성은 임시 기본값이므로 필요에 맞게 조정한다.)
export const TEAM_NAV: Record<string, NavSection[]> = {
  mindcast: NAV_SECTIONS,
  // 국립정신건강센터: 데이터 통합 → 감정 분석 → 지역별 현황 → 조기경보 → 리포트 → 이벤트
  ncmh: pickSections([
    "/home-new",
    "/data-integration-monitoring",
    "/sentiment-analysis",
    "/emotion-preview",
    "/regional-monitoring",
    "/early-warning-system",
    "/auto-generate-report",
    "/social-event",
  ]),
  regional: pickSections([
    "/dashboard",
    "/regional-monitoring",
    "/emotion-keywords",
    "/keyword-search",
    "/social-event",
  ]),
};
